import React, { useContext, useEffect, useState } from 'react'
import PageTitle from '../../Components/PageTitle'
import { ConfigContext } from '../../Context/ConfigContext'
import { GlobalLimitChanger } from '../../Components/InputElements'
import { ContentLoader, NoRecords, TableRows } from '../../Components/Shimmer'
import { DateFormater, TimeFormater } from '../../Components/GlobalFunctions'
import axios from 'axios'
import Swal from 'sweetalert2'
import Flatpickr from 'react-flatpickr'

const emailStatusMap = {
    1: { label: 'Inquiry', badge: 'info' },
    2: { label: 'Newsletter', badge: 'warning' },
    3: { label: 'Contact Us', badge: 'success' },
}

// Defensive read helper – tries the common response field-name variants
const getField = (row, keys, fallback = '') => {
    if (!row) return fallback
    for (const key of keys) {
        if (row[key] !== undefined && row[key] !== null && row[key] !== '') {
            return row[key]
        }
    }
    return fallback
}

const formatDate = (date) => {
    if (!date) return ''
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

// Parse a sender value that may be "Name <email@example.com>" or a plain email address
const parseSender = (value) => {
    if (!value) return { name: '', email: '' }
    const raw = String(value).trim()
    const angle = raw.match(/^(.*?)\s*<([^<>]+)>\s*$/)
    if (angle) return { name: angle[1].trim(), email: angle[2].trim() }
    if (raw.includes('@')) return { name: '', email: raw }
    return { name: raw, email: '' }
}

const InquiryEmails = () => {
    const { primaryColor, apiHeaderJson, apiURL } = useContext(ConfigContext)
    const headers = apiHeaderJson

    const [keyword, setKeyword] = useState('')
    const [emailStatus, setEmailStatus] = useState('') // 1 = Inquiry mails | 2 = Newsletter | 3 = Contact Us
    const [emailReadStatus, setEmailReadStatus] = useState('') // '' = All | 0 = Unread | 1 = Read
    const [emailType, setEmailType] = useState('') // '' = All | 0 = Unread | 1 = Read
    const [fromDate, setFromDate] = useState('')
    const [toDate, setToDate] = useState('')

    const [data, setData] = useState([])
    const [page, setPage] = useState(1)
    const [next, setNext] = useState(false)
    const [prev, setPrev] = useState(false)
    const [totalRecords, setTotalRecords] = useState(0)
    const [totalPages, setTotalPages] = useState(0)
    const [limit, setLimit] = useState(10)
    const [loading, setLoading] = useState(true)
    const [infoLoading, setInfoLoading] = useState(false)
    const [readLoading, setReadLoading] = useState(false)
    const [showModal, setShowModal] = useState(false)
    const [emailInfo, setEmailInfo] = useState(null)

    const getData = async () => {
        try {
            setLoading(true)
            const response = await axios.get(`${apiURL}Masters/GetEmails`, {
                headers,
                params: {
                    keyword,
                    email_status: emailStatus || '',
                    email_read_status: emailReadStatus || '',
                    email_status: emailType || '',
                    from_date: fromDate,
                    to_date: toDate,
                    page,
                    limit,
                },
            })
            const { success, data, page: currentPage, next, prev, total_pages, total_records } = response.data

            if (success) {
                setData(Array.isArray(data) ? data : [])
                setPage(currentPage)
                setNext(next)
                setPrev(prev)
                setTotalPages(total_pages)
                setTotalRecords(total_records)
            }
        } catch (error) {
            console.log('Error listing emails', error)
        } finally {
            setLoading(false)
        }
    }

    const getEmailInfo = async (email_id) => {
        try {
            setInfoLoading(true)
            const response = await axios.get(`${apiURL}Masters/GetEmailInfo`, { headers, params: { email_id } })
            const { success, data } = response.data
            if (success) {
                let new_data = Array.isArray(data) ? data[0] : data ?? null;
                setEmailInfo(new_data)
                if (new_data && new_data.email_read_status === 0) {
                    handleMarkAsRead(email_id);
                }
            }
        } catch (error) {
            console.log('Error fetching email info', error)
        } finally {
            setInfoLoading(false)
        }
    }

    const handleView = (row) => {
        setShowModal(true)
        setEmailInfo(null)
        getEmailInfo(row.email_id)
    }

    const closeModal = () => {
        setShowModal(false)
        setEmailInfo(null)
    }

    const handleMarkAsRead = async (email_id) => {
        if (!email_id) return
        try {
            setReadLoading(true)
            const response = await axios.post(`${apiURL}Masters/ReadEmail`, { email_id }, { headers })
            const { success, message } = response.data

            if (success) {
                // Reflect the change locally and refresh the list
                setEmailInfo(prev =>
                    prev && prev.email_id === email_id
                        ? { ...prev, email_read_status: 1, read_status: 1, is_read: 1 }
                        : prev
                )
                setData(prevData =>
                    prevData.map(row =>
                        row.email_id === email_id
                            ? { ...row, email_read_status: 1, read_status: 1, is_read: 1 }
                            : row
                    )
                )
            }
        } catch (error) {
            console.error('Error marking email as read', error)
            Swal.fire('Error!', 'Something went wrong', 'error')
        } finally {
            setReadLoading(false)
        }
    }

    const handlePrev = () => { if (prev) setPage(prev => prev - 1) }
    const handleNext = () => { if (next) setPage(prev => prev + 1) }
    const handleChange = (e) => setPage(parseInt(e.target.value, 10))

    const resetFilters = () => {
        setKeyword('')
        setEmailStatus(1)
        setEmailReadStatus('')
        setEmailType('')
        setFromDate('')
        setToDate('')
        setPage(1)
    }

    useEffect(() => {
        const delayDebounce = setTimeout(() => getData(), 500)
        return () => clearTimeout(delayDebounce)
    }, [keyword])

    useEffect(() => {
        getData()
    }, [page, limit, emailStatus, emailReadStatus, emailType, fromDate, toDate])

    // ---- Display helpers ----
    const getStatusInfo = (row) => {
        const raw = getField(row, ['email_status', 'status'])
        const status = Number(raw) || 0
        return emailStatusMap[status] || { label: status ? `Status ${status}` : 'Unknown', badge: 'secondary' }
    }

    const isUnread = (row) => {
        if (!row) return false
        const read = getField(row, ['email_read_status', 'read_status', 'is_read'])
        return Number(read) === 0
    }

    const formatMailDate = (value) => {
        if (!value) return '--'
        const date = DateFormater(value)
        let time = ''
        if (/\s|T/.test(value)) {
            try { time = TimeFormater(value) } catch (err) { time = '' }
        }
        if (!date && !time) return value
        return `${date || ''} ${time || ''}`.trim()
    }

    const handleReply = ({ name, email, phone }) => {
        // Build the modal content
        const content = `
    <div style="text-align:left;">
      <p><strong>Name:</strong> ${name || 'N/A'}</p>
      ${email ? `<p><strong>Email:</strong> ${email}</p>` : ''}
      ${phone ? `<p><strong>Phone:</strong> ${phone}</p>` : ''}
    </div>
  `;

        Swal.fire({
            title: 'Reply to Sender',
            html: content,
            showCancelButton: false,
            showDenyButton: true,
            showConfirmButton: true,
            confirmButtonText: '<i class="ri-mail-line me-1"></i> Email',
            denyButtonText: '<i class="ri-phone-line me-1"></i> Call',
            confirmButtonColor: '#0d6efd',
            denyButtonColor: '#198754',
            reverseButtons: true,
            preConfirm: () => {
                // Email action
                if (email) {
                    window.open(`mailto:${email}`, '_blank');
                } else {
                    Swal.fire('No email address available', '', 'info');
                }
            },
            preDeny: () => {
                // Call action
                if (phone) {
                    window.open(`tel:${phone}`, '_blank');
                } else {
                    Swal.fire('No phone number available', '', 'info');
                }
            },
        });
    };

    return (
        <>
            <div className="main-content">
                <div className="page-content">
                    <div className="container-fluid">
                        <PageTitle title="Inquiry Emails" primary="Masters" />
                        {infoLoading && <ContentLoader />}

                        {/* Filters / Search Card */}
                        <div className="row">
                            <div className="col-12">
                                <div className="card" style={{ border: '1px solid #2a326620' }}>
                                    <div className="card-header" style={{ backgroundColor: primaryColor }}>
                                        <h4 className="card-title mb-0 text-white">
                                            <i className="ri-filter-3-line me-2"></i>Filters / Search
                                        </h4>
                                    </div>
                                    <div className="card-body">
                                        <div className="row g-3 align-items-end">
                                            <div className="col-xl-3 col-md-6">
                                                <label className="form-label">Search</label>
                                                <div className="position-relative">
                                                    <input
                                                        type="text"
                                                        className="form-control pe-4"
                                                        placeholder="Search by subject"
                                                        value={keyword}
                                                        onChange={(e) => setKeyword(e.target.value)}
                                                    />
                                                    <span className="position-absolute end-0 top-50 translate-middle-y me-3">
                                                        <i className="ri-search-line"></i>
                                                    </span>
                                                </div>
                                            </div>
                                            <div className="col-xl-3 col-md-6">
                                                <label className="form-label">Email Type</label>
                                                <select
                                                    className="form-select"
                                                    value={emailStatus}
                                                    onChange={(e) => { setEmailStatus(parseInt(e.target.value, 10) || ''); setPage(1) }}
                                                >
                                                    <option value="">All Mails</option>
                                                    <option value={1}>Inquiry Mails</option>
                                                    <option value={2}>Newsletter Mails</option>
                                                    <option value={3}>Contact Us Mails</option>
                                                </select>
                                            </div>
                                            <div className="col-xl-3 col-md-6">
                                                <label className="form-label">Read Status</label>
                                                <select
                                                    className="form-select"
                                                    value={emailReadStatus}
                                                    onChange={(e) => { setEmailReadStatus(e.target.value); setPage(1) }}
                                                >
                                                    <option value="">All</option>
                                                    <option value={0}>Unread</option>
                                                    <option value={1}>Read</option>
                                                </select>
                                            </div>
                                            <div className="col-xl-3 col-md-6">
                                                <label className="form-label">Email Type</label>
                                                <select
                                                    className="form-select"
                                                    value={emailType}
                                                    onChange={(e) => { setEmailType(e.target.value); setPage(1) }}
                                                >
                                                    <option value="">All</option>
                                                    <option value={1}>Inquiry</option>
                                                    <option value={2}>Newsletter</option>
                                                    <option value={3}>Contact Us</option>
                                                </select>
                                            </div>
                                            <div className="col-xl-3 col-md-6">
                                                <label className="form-label">From Date</label>
                                                <Flatpickr
                                                    value={fromDate}
                                                    options={{ dateFormat: 'Y-m-d' }}
                                                    onChange={(dates) => { setFromDate(formatDate(dates[0])); setPage(1) }}
                                                    className="form-control"
                                                    placeholder="From Date"
                                                />
                                            </div>
                                            <div className="col-xl-3 col-md-6">
                                                <label className="form-label">To Date</label>
                                                <Flatpickr
                                                    value={toDate}
                                                    options={{ dateFormat: 'Y-m-d' }}
                                                    onChange={(dates) => { setToDate(formatDate(dates[0])); setPage(1) }}
                                                    className="form-control"
                                                    placeholder="To Date"
                                                />
                                            </div>
                                            <div className="col-xl-3 col-md-6">
                                                <button type="button" className="btn btn-soft-primary w-100" onClick={resetFilters}>
                                                    <i className="ri-loop-left-line me-1"></i>Clear Filters
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Emails List Card */}
                        <div className="row">
                            <div className="col-12">
                                <div className="card">
                                    <div className="card-header" style={{ backgroundColor: primaryColor }}>
                                        <h4 className="card-title mb-0 text-white">
                                            <i className="ri-mail-line me-2"></i>Emails List
                                        </h4>
                                    </div>
                                    <div className="card-body">
                                        <div className="table-responsive table-card">
                                            <table className="table table-bordered table-striped table-nowrap table-hover mb-0">
                                                {loading ? (
                                                    <TableRows rows="10" colspan="8" />
                                                ) : (
                                                    <>
                                                        <thead className="table-light text-center">
                                                            <tr>
                                                                <th>#</th>
                                                                <th>Sender</th>
                                                                <th>Subject</th>
                                                                <th>Product</th>
                                                                <th>Type</th>
                                                                <th>Read Status</th>
                                                                <th>Date</th>
                                                                <th>Actions</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {data.length > 0 ? (
                                                                data.map((row, index) => {
                                                                    const sender = parseSender(getField(row, ['email_from']))
                                                                    const senderName = sender.name || sender.email || getField(row, ['email_name', 'sender_name', 'name', 'from_name']) || '--'
                                                                    const senderEmail = sender.email
                                                                    const phone = getField(row, ['email_phone_no', 'email_phone', 'phone', 'mobile', 'contact_number'])
                                                                    const subject = getField(row, ['email_subject', 'subject']) || 'No Subject'
                                                                    const message = getField(row, ['email_message', 'message', 'email_description', 'description', 'email_content', 'email_body'])
                                                                    const productName = getField(row, ['product_name', 'product']) || '--'
                                                                    const statusInfo = getStatusInfo(row)
                                                                    const unread = isUnread(row)
                                                                    const dateStr = formatMailDate(getField(row, ['created_date', 'email_date', 'created_at', 'email_datetime']))

                                                                    return (
                                                                        <tr key={row.email_id || index} className={unread ? 'table-active' : ''}>
                                                                            <td className="text-center">{((page - 1) * limit) + index + 1}</td>
                                                                            <td>
                                                                                <div className="d-flex justify-content-between align-items-start w-100">
                                                                                    <div className="d-flex flex-column">
                                                                                        <span className={unread ? 'fw-semibold' : ''}>{senderName}</span>
                                                                                        {senderEmail && senderEmail !== senderName && <small className="text-muted">{senderEmail}</small>}
                                                                                        {phone && <small className="text-muted"><i className="ri-phone-line me-1"></i>{phone}</small>}
                                                                                    </div>

                                                                                    {/* Reply icon button */}
                                                                                    <button
                                                                                        className="btn btn-sm btn-outline-primary rounded-circle p-1 ms-2"
                                                                                        onClick={() => handleReply({ name: senderName, email: senderEmail, phone })}
                                                                                        title="Reply to this sender"
                                                                                        style={{ width: '30px', height: '30px' }}
                                                                                    >
                                                                                        <i className="ri-reply-line"></i>
                                                                                    </button>
                                                                                </div>
                                                                            </td>
                                                                            <td>
                                                                                <div style={{ maxWidth: 280 }}>
                                                                                    <p className={`mb-0 ${unread ? 'fw-semibold' : ''}`}>{subject}</p>
                                                                                    {message && <small className="text-muted d-block text-truncate">{message}</small>}
                                                                                </div>
                                                                            </td>
                                                                            <td>
                                                                                <div style={{ maxWidth: 220 }}>
                                                                                    <span className={unread ? 'fw-semibold' : ''}>{productName}</span>
                                                                                </div>
                                                                            </td>
                                                                            <td className="text-center">
                                                                                <span className={`badge bg-${statusInfo.badge}-subtle text-${statusInfo.badge}`}>{statusInfo.label}</span>
                                                                            </td>
                                                                            <td className="text-center">
                                                                                {unread ? (
                                                                                    <span className="badge bg-danger-subtle text-danger">
                                                                                        <i className="ri-mail-unread-line me-1"></i>Unread
                                                                                    </span>
                                                                                ) : (
                                                                                    <span className="badge bg-success-subtle text-success">
                                                                                        <i className="ri-mail-check-fill me-1"></i>Read
                                                                                    </span>
                                                                                )}
                                                                            </td>
                                                                            <td className="text-center">{dateStr}</td>
                                                                            <td className="text-center">
                                                                                <div className="d-flex gap-2 justify-content-center">
                                                                                    <button
                                                                                        type="button"
                                                                                        className="btn btn-sm btn-soft-primary"
                                                                                        title="View Email"
                                                                                        onClick={() => handleView(row)}
                                                                                    >
                                                                                        <i className="ri-eye-line"></i>
                                                                                    </button>
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                })
                                                            ) : (
                                                                <tr>
                                                                    <td colSpan={8}>
                                                                        <NoRecords />
                                                                    </td>
                                                                </tr>
                                                            )}
                                                        </tbody>
                                                        <tfoot className='table-light'>
                                                            <tr>
                                                                <th colSpan={8}>
                                                                    <div className="d-flex justify-content-between">
                                                                        <button
                                                                            disabled={!prev && !loading}
                                                                            type="button"
                                                                            onClick={handlePrev}
                                                                            className="btn btn-warning btn-label waves-effect waves-light"
                                                                        >
                                                                            <i className="ri-arrow-left-line label-icon align-middle fs-16 me-2" /> Previous
                                                                        </button>
                                                                        <div className='col-md-4 d-flex align-items-center justify-content-center'>
                                                                            <small>Total Records: {totalRecords} | Total Pages: {totalPages} | Current Page: {page}</small>
                                                                        </div>
                                                                        <div className='col-md-2'>
                                                                            <select className="form-select" value={page} onChange={handleChange}>
                                                                                {Array.from({ length: totalPages }, (_, i) => (
                                                                                    <option key={i + 1} value={i + 1}>{i + 1}</option>
                                                                                ))}
                                                                            </select>
                                                                        </div>
                                                                        <div className='col-md-2'>
                                                                            <GlobalLimitChanger
                                                                                placeholder="Set limit:"
                                                                                name="globalLimit"
                                                                                value={limit}
                                                                                onChange={setLimit}
                                                                                showAllValue={totalRecords}
                                                                            />
                                                                        </div>
                                                                        <button
                                                                            disabled={!next && !loading}
                                                                            type="button"
                                                                            onClick={handleNext}
                                                                            className="btn btn-primary btn-label waves-effect right waves-light"
                                                                        >
                                                                            <i className="ri-arrow-right-line label-icon align-middle fs-16 ms-2" /> Next
                                                                        </button>
                                                                    </div>
                                                                </th>
                                                            </tr>
                                                        </tfoot>
                                                    </>
                                                )}
                                            </table>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Email Detail Modal */}
                        {showModal && (
                            <div
                                className="modal fade show d-block"
                                tabIndex="-1"
                                role="dialog"
                                style={{ display: 'block', backgroundColor: 'rgba(0, 0, 0, 0.45)' }}
                                onClick={closeModal}
                            >
                                <div
                                    className="modal-dialog modal-dialog-centered modal-dialog-scrollable modal-lg"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    <div className="modal-content">
                                        <div className="modal-header" style={{ backgroundColor: primaryColor }}>
                                            <h5 className="modal-title text-white">
                                                <i className="ri-mail-open-line me-2"></i>Email Details
                                            </h5>
                                            <button type="button" className="btn-close" onClick={closeModal}></button>
                                        </div>
                                        <div className="modal-body">
                                            {emailInfo ? (
                                                <>
                                                    {(() => {
                                                        const infoStatus = getStatusInfo(emailInfo)
                                                        const infoUnread = isUnread(emailInfo)
                                                        const infoSender = parseSender(getField(emailInfo, ['email_from']))
                                                        const infoName = infoSender.name || infoSender.email || getField(emailInfo, ['email_name', 'sender_name', 'name', 'from_name']) || '--'
                                                        const infoEmail = infoSender.email
                                                        const infoPhone = getField(emailInfo, ['email_phone_no', 'email_phone', 'phone', 'mobile', 'contact_number'])
                                                        const infoProduct = getField(emailInfo, ['product_name', 'product']) || '--'
                                                        const infoSubject = getField(emailInfo, ['email_subject', 'subject']) || 'No Subject'
                                                        const infoMessage = getField(emailInfo, ['email_message', 'message', 'email_description', 'description', 'email_content', 'email_body'])
                                                        const infoDate = formatMailDate(getField(emailInfo, ['created_date', 'email_date', 'created_at', 'email_datetime']))

                                                        return (
                                                            <>
                                                                <div className="row mb-3">
                                                                    <div className="col-md-6">
                                                                        <small className="text-muted d-block">From</small>
                                                                        <strong>{infoName}</strong>
                                                                        {infoEmail && infoEmail !== infoName && (
                                                                            <div>
                                                                                <a href={`mailto:${infoEmail}`} className="text-primary">{infoEmail}</a>
                                                                            </div>
                                                                        )}
                                                                        {infoPhone && <div className="text-muted"><i className="ri-phone-line me-1"></i>{infoPhone}</div>}
                                                                    </div>
                                                                    <div className="col-md-6 text-md-end mt-2 mt-md-0">
                                                                        <small className="text-muted d-block">Received On</small>
                                                                        <strong>{infoDate}</strong>
                                                                    </div>
                                                                </div>
                                                                <div className="mb-3">
                                                                    <small className="text-muted d-block">Subject</small>
                                                                    <h5 className="mb-0">{infoSubject}</h5>
                                                                </div>
                                                                {infoProduct && infoProduct !== '--' && (
                                                                    <div className="mb-3">
                                                                        <small className="text-muted d-block">Product</small>
                                                                        <h6 className="mb-0">
                                                                            <i className="ri-shopping-bag-3-line me-1 text-primary"></i>{infoProduct}
                                                                        </h6>
                                                                    </div>
                                                                )}
                                                                <div className="border rounded p-3 mb-3" style={{ backgroundColor: '#f8f9fa', whiteSpace: 'pre-wrap' }}>
                                                                    <small className="text-muted d-block mb-2">Message</small>
                                                                    {infoMessage || '--'}
                                                                </div>
                                                                <div className="row align-items-center">
                                                                    <div className="col-md-6">
                                                                        <small className="text-muted d-block">Type</small>
                                                                        <span className={`badge bg-${infoStatus.badge}-subtle text-${infoStatus.badge}`}>{infoStatus.label}</span>
                                                                    </div>
                                                                    <div className="col-md-6 text-md-end mt-2 mt-md-0">
                                                                        <small className="text-muted d-block">Read Status</small>
                                                                        {infoUnread ? (
                                                                            <span className="badge bg-danger-subtle text-danger"><i className="ri-mail-unread-line me-1"></i>Unread</span>
                                                                        ) : (
                                                                            <span className="badge bg-success-subtle text-success"><i className="ri-mail-check-fill me-1"></i>Read</span>
                                                                        )}
                                                                    </div>
                                                                </div>
                                                            </>
                                                        )
                                                    })()}
                                                </>
                                            ) : (
                                                <p className="text-muted text-center py-4">Email not found.</p>
                                            )}
                                        </div>
                                        <div className="modal-footer">
                                            <button type="button" className="btn btn-light" onClick={closeModal}>Close</button>
                                            <button
                                                type="button"
                                                className="btn btn-primary"
                                                disabled={!emailInfo || !isUnread(emailInfo) || readLoading}
                                                onClick={() => handleMarkAsRead(emailInfo?.email_id)}
                                            >
                                                <i className="ri-mail-check-line me-1"></i>
                                                {readLoading ? 'Please wait...' : 'Mark as Read'}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </>
    )
}

export default InquiryEmails